import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling, paid, expired, error
    const [attempts, setAttempts] = useState(0);
    const { refresh } = useAuth();

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let cancelled = false;
        let count = 0;
        const max = 8;
        const poll = async () => {
            count++;
            setAttempts(count);
            try {
                const r = await api.get(`/billing/checkout-status?session_id=${sessionId}`);
                if (cancelled) return;
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    await refresh();
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (count < max) {
                    setTimeout(poll, 2500);
                } else {
                    setStatus("error");
                }
            } catch {
                if (count < max) setTimeout(poll, 2500);
                else setStatus("error");
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen bg-bone grid place-items-center px-4">
            <div className="card-soft p-10 max-w-md w-full text-center" data-testid="billing-success-page">
                {status === "polling" && (
                    <>
                        <Loader2 size={48} className="text-forest animate-spin mx-auto mb-4" />
                        <h1 className="font-display text-2xl text-forest-dark mb-2">Verifica pagamento...</h1>
                        <p className="text-sm text-stone-500">Tentativo {attempts}/8</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 size={56} className="text-forest mx-auto mb-4" />
                        <h1 className="font-display text-2xl text-forest-dark mb-2">Pagamento riuscito! 🎉</h1>
                        <p className="text-stone-600 mb-6">
                            Il tuo abbonamento è attivo. Grazie per supportare HACCP Manager.
                        </p>
                        <Link to="/" className="btn-primary inline-flex items-center gap-2">
                            Torna alla dashboard
                        </Link>
                    </>
                )}
                {status === "expired" && (
                    <>
                        <XCircle size={56} className="text-orange-500 mx-auto mb-4" />
                        <h1 className="font-display text-2xl text-orange-700 mb-2">Sessione scaduta</h1>
                        <p className="text-stone-600 mb-6">Riprova dal menu Abbonamento.</p>
                        <Link to="/billing" className="btn-primary">Torna al piano</Link>
                    </>
                )}
                {status === "error" && (
                    <>
                        <XCircle size={56} className="text-red-500 mx-auto mb-4" />
                        <h1 className="font-display text-2xl text-red-700 mb-2">Errore</h1>
                        <p className="text-stone-600 mb-6">
                            Non riusciamo a verificare il pagamento. Controlla la tua email per la ricevuta o contatta l'assistenza.
                        </p>
                        <Link to="/billing" className="btn-primary">Torna al piano</Link>
                    </>
                )}
            </div>
        </div>
    );
}
